import { API_ENDPOINT } from '../config';

async function signupUser(email: string, password: string, displayName: string) {
    try {
        const response = await fetch(API_ENDPOINT.signupUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, displayName }),
        });

        // Handle non-200 responses
        if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Failed to sign up';

        // Check for specific error messages
        switch (response.status) {
            case 400:
            errorMessage = errorData.error || 'Invalid email or password format';
            break;
            case 401:
            errorMessage = errorData.error || 'Invalid authentication';
            break;
            case 409:
            errorMessage = errorData.error || 'User already exists';
            break;
            case 500:
            errorMessage = errorData.error || 'Internal server error';
            break;
            default:
            errorMessage = errorData.error || 'An unexpected error occurred';
        }

        return { error: errorMessage, data: null };
        }

        const data = await response.json();
        return { error: null, data };
    } catch (error) {
        return { error: (error as Error).message, data: null };
    }
}

async function loginUser(email: string, password: string) {
    try {
      const response = await fetch(API_ENDPOINT.loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 401) {
        return { data: null, error: 'Unauthorized: Invalid email or password' };
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to log in');
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
}

async function loginUserWithToken(token: string) {
  try {
    const response = await fetch(API_ENDPOINT.loginWithTokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (response.status === 401) {
      return { data: null, error: 'Unauthorized: Invalid email or password' };
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to log in');
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: (error as Error).message };
  }
}

async function sendResetPasswordLink(email: string) {
  try {
    const response = await fetch(API_ENDPOINT.resetPasswordUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (response.status === 404) {
      return { success: false, message: 'User not found' };
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send password reset link');
    }

    return { success: true, message: 'Password reset link sent successfully' };
  } catch (error) {
    return { success: false, message: (error as Error).message };
  }
}

async function addCreditEntry(uid: string, credit: number) {
  try {
    const response = await fetch(API_ENDPOINT.initialCreditsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uid, credit }),
    });

    // Handle non-200 responses
    if (!response.ok) {
      const errorData = await response.json();
      let errorMessage = "Failed to add credit entry";

      // Check for specific error messages
      switch (response.status) {
        case 400:
          errorMessage = errorData.error || "Invalid input data";
          break;
        case 401:
          errorMessage = errorData.error || "Unauthorized access";
          break;
        case 405:
          errorMessage = errorData.error || "Method not allowed";
          break;
        case 500:
          errorMessage = errorData.error || "Internal server error";
          break;
        default:
          errorMessage = errorData.error || "An unexpected error occurred";
      }

      return { error: errorMessage, data: null };
    }

    const data = await response.json();
    return { error: null, data };
  } catch (error) {
    return { error: (error as Error).message, data: null };
  }
}

export { signupUser, loginUser, loginUserWithToken, sendResetPasswordLink, addCreditEntry }
